import axios, { AxiosError, type AxiosInstance } from 'axios'
import { showError } from '@/lib/toast'
import { INVALID_UUID_ERROR_CODE } from '@/constants/api'
import { HTTP_STATUS } from '@/constants/http-status'
import { HTTP_TIMEOUT_MS } from '@/constants/timing'
import {
  captureApiError,
  addSentryBreadcrumb,
  safeParseRequestData,
} from '@/configs/sentry.config'

/**
 * Per-request options this client understands.
 *
 * Declared on axios's own config rather than cast at each call site, so a
 * caller opting out of the shared toast is type-checked like any other option.
 */
declare module 'axios' {
  export interface AxiosRequestConfig {
    /** This caller reports its own failures; the interceptor stays quiet. */
    skipErrorToast?: boolean
    /** A GET 404 is handled by the caller instead of redirecting to not-found. */
    skipNotFoundRedirect?: boolean
  }
}

interface ApiError {
  message: string
  statusCode?: number
  errors?: Record<string, string[]>
}

type UnauthorizedRedirectHandler = () => void

type NotFoundRedirectHandler = () => void

let unauthorizedRedirectHandler: UnauthorizedRedirectHandler | null = null

let notFoundRedirectHandler: NotFoundRedirectHandler | null = null

let tokenGetter: (() => Promise<string | null>) | null = null

const setUnauthorizedRedirectHandler = (
  handler: UnauthorizedRedirectHandler | null
) => {
  unauthorizedRedirectHandler = handler
}

const setNotFoundRedirectHandler = (
  handler: NotFoundRedirectHandler | null
) => {
  notFoundRedirectHandler = handler
}

const setTokenGetter = (getter: (() => Promise<string | null>) | null) => {
  tokenGetter = getter
}

const defaultUnauthorizedRedirect = () => {
  if (window.location.pathname !== '/') {
    window.location.href = '/'
  }
}

const defaultNotFoundRedirect = () => {
  window.location.href = '/$'
}

// In dev, route localhost-targeted requests through the Vite proxy at
// `/api` so they become same-origin and skip the CORS OPTIONS preflight
// (see vite.config.ts). When VITE_API_URL points at a non-local backend
// (e.g., a cloud env), we honor it as-is and the browser handles CORS.
const rawApiUrl = import.meta.env.VITE_API_URL ?? ''
const isLocalBackend = /\/\/(localhost|127\.0\.0\.1):3000\/?$/.test(rawApiUrl)

const API_CONFIG = {
  baseURL: import.meta.env.DEV && isLocalBackend ? '/api' : rawApiUrl,
  timeout: HTTP_TIMEOUT_MS,
} as const

const createHttpClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor - Add auth token and version
  client.interceptors.request.use(
    async (config) => {
      // Check if this request should skip auth (for public endpoints)
      const skipAuth = (config as { skipAuth?: boolean })?.skipAuth

      // Get token from Clerk via the token getter
      if (tokenGetter && !skipAuth) {
        try {
          const token = await tokenGetter()
          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          } else {
            // No token available - redirect to login
            console.warn('No auth token available, redirecting to login')
            const redirectHandler =
              unauthorizedRedirectHandler || defaultUnauthorizedRedirect
            redirectHandler()
            return Promise.reject(new Error('No auth token available'))
          }
        } catch (error) {
          console.error('Failed to get auth token:', error)
          // Token fetch failed - redirect to login
          const redirectHandler =
            unauthorizedRedirectHandler || defaultUnauthorizedRedirect
          redirectHandler()
          return Promise.reject(error)
        }
      }

      addSentryBreadcrumb(
        `API Request: ${config.method?.toUpperCase()} ${config.url}`,
        'http',
        {
          url: config.url,
          method: config.method,
        }
      )

      return config
    },
    (error) => Promise.reject(error)
  )

  // Response interceptor - Handle common errors
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const statusCode = error.response?.status
      const endpoint = error.config?.url || 'unknown'
      const method = error.config?.method?.toUpperCase() || 'unknown'

      // Capture critical errors in Sentry (5xx, 401, 403, network errors)
      const isCriticalError =
        !statusCode ||
        statusCode === HTTP_STATUS.UNAUTHORIZED ||
        statusCode === HTTP_STATUS.FORBIDDEN ||
        statusCode >= HTTP_STATUS.INTERNAL_SERVER_ERROR

      if (isCriticalError) {
        captureApiError(error, {
          endpoint,
          method,
          statusCode,
          requestData: safeParseRequestData(error.config?.data),
        })
      }

      // Handle 401 Unauthorized
      if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
        const redirectHandler =
          unauthorizedRedirectHandler || defaultUnauthorizedRedirect
        redirectHandler()
        return Promise.reject(error)
      }

      const errorData = error.response?.data as
        | { code?: string; error?: { code?: string } }
        | undefined

      const errorCode = errorData?.code || errorData?.error?.code
      const skipNotFoundRedirect = error.config?.skipNotFoundRedirect

      // Handle 404 Not Found — only redirect for GET requests. This redirect
      // fires for ANY GET 404 (page loads and data queries alike) unless the
      // caller opts out with `skipNotFoundRedirect`. Mutation 404s
      // (POST/PATCH/DELETE) fall through to the toast so a missing endpoint
      // or stale record doesn't bounce the user to /not-found.
      const requestMethod = error.config?.method?.toLowerCase()
      if (
        (statusCode === HTTP_STATUS.NOT_FOUND ||
          errorCode === INVALID_UUID_ERROR_CODE) &&
        requestMethod === 'get'
      ) {
        if (skipNotFoundRedirect) {
          return Promise.reject(error)
        }

        const redirectHandler =
          notFoundRedirectHandler || defaultNotFoundRedirect
        redirectHandler()
        return Promise.reject(error)
      }

      // Skip toast for 409 Conflict - let components handle inline (e.g., duplicate name)
      if (statusCode === HTTP_STATUS.CONFLICT) {
        return Promise.reject(error)
      }

      // A caller that says it will report the failure itself gets no toast from
      // here. Without this the interceptor fires one and the caller fires a
      // second, and since `showError` prefers the backend message over the
      // caller's, the more specific of the two is the one nobody reads.
      if (!error.config?.skipErrorToast) showError(error)

      if (!error.response) {
        console.error('Network error:', error.message)
      }

      return Promise.reject(error)
    }
  )

  return client
}

const httpClient = createHttpClient()

const getApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    return {
      message:
        error.response?.data?.message || error.message || 'An error occurred',
      statusCode: error.response?.status,
      errors: error.response?.data?.errors,
    }
  }
  return {
    message:
      error instanceof Error ? error.message : 'An unknown error occurred',
  }
}

export type { ApiError, UnauthorizedRedirectHandler, NotFoundRedirectHandler }
export {
  httpClient,
  createHttpClient,
  getApiError,
  setUnauthorizedRedirectHandler,
  setNotFoundRedirectHandler,
  setTokenGetter,
}
